import { useEffect } from "react";
import { Box, Container, Typography } from "@mui/material";
import { WahooWorkoutType } from "@WahooFitness/cloud-client-types";
import CustomizeProfileSection from "./CustomizeProfileSection";
import ZonesSection from "./ZonesSection";
import { t } from "@lingui/macro";
import { getWorkoutTypeNames } from "../AddWorkoutProfile/WorkoutTypeNames";
import Wrapper from "./EditWorkoutProfileWrapper";
import WorkoutSettingsSection from "./WorkoutSettingsSection";
import { useSearchParams } from "react-router-dom";
import { GlobalHeader, useDialogContext } from "@WahooFitness/wahoo-offline-mfe";
import { WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import ElemntSection from "./ElemntSection";
import SensorsSection from "./SensorsSection";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { useCheckForProfileCorruption } from "../useCheckForProfileCorruption";
import ProfilePreferencesSection from "./ProfilePreferencesSection";
import { getWorkoutTypeIcon } from "@/services/getWorkoutTypeIcon";
import useDrawerPresentation from "@/hooks/useDrawerPresentation";

interface Props {
  title?: string;
  profile?: WSMWorkoutProfile;
  backAction?: () => void;
  headerAction?: React.ReactElement;
  containedInDialog?: boolean;
  warningDialogOpen?: boolean;
  onDiscardWorkoutProfile?: () => void;
  onContinueEditing?: () => void;
}

const EditWorkoutProfile = ({
  title,
  profile,
  backAction,
  headerAction,
  containedInDialog,
  warningDialogOpen,
  onDiscardWorkoutProfile,
  onContinueEditing,
}: Props) => {
  const [searchParams] = useSearchParams();
  const { isDrawer, close } = useDrawerPresentation("editWorkoutProfile");

  useCheckForProfileCorruption(profile);

  const workoutTypeNames = getWorkoutTypeNames();
  const workoutTypeName =
    profile?.workout_type_id !== undefined
      ? workoutTypeNames[WahooWorkoutType[profile?.workout_type_id]]
      : "";

  const { setDialog, handleClose } = useDialogContext();

  useEffect(() => {
    if (warningDialogOpen) {
      setDialog({
        open: true,
        title: t`Discard new workout profile?`,
        body: t`Are you sure you want to discard your new workout profile?`,
        actions: [
          { text: t`Discard`, action: onDiscardWorkoutProfile, color: "error" },
          { text: t`Continue editing`, action: onContinueEditing },
        ],
      });
    } else {
      handleClose();
    }
  }, [handleClose, onContinueEditing, onDiscardWorkoutProfile, setDialog, warningDialogOpen]);

  const { hasAssociatedElemnt } = useAssociatedDevices();

  const WorkoutTypeIcon = getWorkoutTypeIcon(profile?.workout_type_id);

  return (
    <>
      {!containedInDialog && (
        <GlobalHeader
          title={title || profile?.name || ""}
          primaryLeftActionItem={
            isDrawer ? { icon: <WfIconNavigationCloseRegular />, action: close } : undefined
          }
          backAction={backAction ?? (isDrawer ? close : undefined)}
          primaryRightActionItem={{ componentOverride: headerAction }}
        />
      )}
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Wrapper containedInDialog={containedInDialog}>
          <Box
            display="flex"
            flexDirection="column"
            overflow="auto"
            py={2}
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            {profile?.name && (
              <Box p={2} gap={1} display="flex" alignItems="center">
                <WorkoutTypeIcon sx={{ width: "40px", height: "40px" }} />
                <Typography variant="prose-3xl-bold">{profile.name}</Typography>
              </Box>
            )}
            <ProfilePreferencesSection profile={profile} workoutTypeName={workoutTypeName} />
            {searchParams.get("dev") === "true" && <CustomizeProfileSection />}
            <WorkoutSettingsSection
              profile={profile}
              containedInDialog={containedInDialog}
              hasElemntPaired={hasAssociatedElemnt}
            />
            {hasAssociatedElemnt && <ElemntSection profile={profile} />}
            <SensorsSection profile={profile} />
            <ZonesSection profile={profile} />
          </Box>
        </Wrapper>
      </Container>
    </>
  );
};

export default EditWorkoutProfile;
